import { observable, extendObservable, action, computed } from "mobx";

/**
 * Inspired by react-reformed.
 */
class FormStore {
  changeHandlers = [];

  @observable model = {};

  @observable initialModel = {};

  /**
   * Will track those props which corresponding widget has blurred (focused away / lost focus)
   */
  @observable blurred = {};

  constructor(initialModel = {}) {
    this.initialModel = initialModel;
    this.model = observable({ ...initialModel });

    this.onChange = this.onChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.setProperty = this.setProperty.bind(this);
    this.setModel = this.setModel.bind(this);
    this.bindInput = this.bindInput.bind(this);
    this.getAggregatedError = this.getAggregatedError.bind(this);
  }

  @action
  reset() {
    this.model = observable({ ...this.initialModel });
    this.blurred = {};
  }

  @action
  addChangeHandler(callback, leading = true) {
    this.changeHandlers.push(callback);

    if (leading) {
      this.notifyChange(null);
    }
  }

  notifyChange() {
    for (let i = 0; i < this.changeHandlers.length; i += 1) {
      const handler = this.changeHandlers[i];
      // defer so it's non blocking.
      // Note, maybe in future it might be handy to pass which property changed.
      setTimeout(() => handler(this.model), 0);
    }
  }

  @computed
  get isDirty() {
    const properties = Object.keys(this.initialModel);
    const modelProperties = Object.keys(this.model);

    for (let i = 0; i < properties.length; i += 1) {
      const property = properties[i];

      // do not compare if the property is not set on the actual model.
      if (modelProperties.indexOf(property) === -1) {
        // eslint-disable-next-line no-continue
        continue;
      }

      if (this.model[property] !== this.initialModel[property]) {
        return true;
      }
    }

    return false;
  }

  @action
  onChange(event) {
    this.setProperty(event.target.name, event.target.value);
  }

  @action
  onBlur(event) {
    this.blurred[event.target.name] = true;
  }

  @action
  setProperty(property, value) {
    const modelCopy = { ...this.model };

    // note: checking typeof this.model[property] === "undefined" won't always work.
    if (Object.keys(modelCopy).indexOf(property) !== -1) {
      modelCopy[property] = value;
    } else {
      extendObservable(modelCopy, {
        [property]: value,
      });
    }

    // in case the property was not declared in the initialModel, let's create an default initial property
    // to help the isDirty computed function work properly.
    if (Object.keys(this.initialModel).indexOf(property) === -1) {
      // we use empty string instead of null or undefined since the formStore is meant to work with html form
      // values which are all technically of type string.
      this.initialModel[property] = "";
    }

    this.model = modelCopy;

    this.notifyChange();

    return this.model;
  }

  @action
  setModel(model) {
    Object.keys(model).forEach(key => {
      this.setProperty(key, model[key]);
    });

    return this.model;
  }

  /**
   * @property {string} name The name of the input element
   * @property {object} validation an object representing the validation result returned by utils/ReactReformed/ValidateSchema
   */
  @action
  bindInput(name, validation = null, serverValidation = null) {
    const value = this.model[name];

    const bindings = {
      name,
      // cast null / undefined to empty string. Any html input value can't be null
      value: value === undefined || value === "" || value === null ? "" : value,
      onChange: this.onChange,
      onBlur: this.onBlur,
      error: this.getAggregatedError(name, validation, serverValidation),
    };

    return bindings;
  }

  getAggregatedError(name, validation = null, serverValidation = null, { showErrorsOnBlur = true } = {}) {
    // local validation takes precedence since it's transient; serverValidation won't get cleared until
    // the form is submitted again.

    // show local validations only if the corresponding html control has lost focus (blurred).
    if (validation && validation.fields[name] && (this.blurred[name] || showErrorsOnBlur === false)) {
      const field = validation.fields[name];
      // bind an error prop is the input has lost focus and the input has errors. This way we ensure to
      // show an errors only upon user interaction, not before.

      // This if is necessary so we don't return "false" too early, in case there's a server error to display.
      // If it's valid locally, let's the code to reach the serverValidation check below.
      if (!field.isValid) {
        if (field.errors.length > 0) {
          return { content: field.errors[0], pointing: "above" };
        }

        return true;
      }
    }

    if (serverValidation && serverValidation[name]) {
      const errorMessage = serverValidation[name];
      // Make sure the error message is a string, this way we can show an nice semantic ui error tooltip.
      // otherwise let's just bind a boolean so the input widget gets highlighted in red.
      return typeof errorMessage === "string" ? { content: errorMessage, pointing: "above" } : true;
    }

    return undefined;
  }
}

export default FormStore;
